import { format, isToday, isYesterday, differenceInCalendarDays, parseISO, isValid } from "date-fns";
import { es } from "date-fns/locale";
import { env } from "../../../Utils/Environment";

/** Base pública donde el backend sirve la media de las conversaciones. */
export const MEDIA_BASE_URL = (() => {
  const base = env("MARKETSMS_MEDIA_URL", "https://api.marketsms.co/");
  return base.endsWith("/") ? base : base + "/";
})();

export type MediaKind = "image" | "video" | "audio" | "document" | "other";

/** Convierte "yyyy-MM-dd HH:mm:ss" (formato del backend) en Date de forma segura. */
export function parseDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = parseISO(value.includes("T") ? value : value.replace(" ", "T"));
  return isValid(date) ? date : null;
}

/** Hora corta para burbujas: "14:05". */
export function formatHour(value?: string | null): string {
  const date = parseDate(value);
  return date ? format(date, "HH:mm") : "";
}

/** Etiqueta de separador de día: "Hoy", "Ayer", "lunes", "12 de marzo de 2026". */
export function formatDayLabel(date: Date): string {
  if (isToday(date)) return "Hoy";
  if (isYesterday(date)) return "Ayer";
  if (differenceInCalendarDays(new Date(), date) < 7) return capitalize(format(date, "EEEE", { locale: es }));
  return format(date, "d 'de' MMMM 'de' yyyy", { locale: es });
}

/** Hora relativa para la lista: "14:05", "Ayer", "lunes", "12/03/26". */
export function formatListDate(value?: string | null): string {
  const date = parseDate(value);
  if (!date) return "";
  if (isToday(date)) return format(date, "HH:mm");
  if (isYesterday(date)) return "Ayer";
  if (differenceInCalendarDays(new Date(), date) < 7) return capitalize(format(date, "EEEE", { locale: es }));
  return format(date, "dd/MM/yy");
}

export function dayKey(value?: string | null): string {
  const date = parseDate(value);
  return date ? format(date, "yyyy-MM-dd") : "";
}

function capitalize(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** URL absoluta para media guardada en el servidor (o la URL local si es blob/data/http). */
export function resolveMediaUrl(path?: string | null): string {
  if (!path) return "";
  if (/^(blob:|data:|https?:\/\/)/i.test(path)) return path;
  return MEDIA_BASE_URL + path.replace(/^\/+/, "");
}

export function getMediaKind(url?: string | null, contentType?: string | null): MediaKind {
  if (contentType) {
    if (contentType === "image" || contentType === "sticker") return "image";
    if (contentType === "video") return "video";
    if (contentType === "audio") return "audio";
    if (contentType === "document") return "document";
  }
  if (!url) return "other";
  const clean = url.split("?")[0].split("#")[0];
  const ext = clean.split(".").pop()?.toLowerCase() ?? "";
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
  if (["mp4", "3gp", "mov"].includes(ext)) return "video";
  if (["mp3", "wav", "ogg", "opus", "m4a", "aac", "amr", "webm"].includes(ext)) return "audio";
  if (["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv", "zip"].includes(ext)) return "document";
  return "other";
}

export function fileKind(file: File): MediaKind {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("audio/")) return "audio";
  return "document";
}

export function fileNameFromUrl(url?: string | null): string {
  if (!url) return "Archivo";
  const clean = url.split("?")[0].split("#")[0];
  return decodeURIComponent(clean.split("/").pop() || "Archivo");
}

/** Escapa HTML antes de aplicar el formato de WhatsApp (evita inyección de HTML/XSS). */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Formato de WhatsApp (*negrita*, _cursiva_, ~tachado~, ```mono```) sobre texto ya escapado. */
export function formatWhatsappText(text: string): string {
  let html = escapeHtml(text);
  html = html.replace(/```([\s\S]+?)```/g, "<code>$1</code>");
  html = html.replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s).,!?:;]|$)/g, "$1<strong>$2</strong>");
  html = html.replace(/(^|[\s(])_([^_\n]+)_(?=[\s).,!?:;]|$)/g, "$1<em>$2</em>");
  html = html.replace(/(^|[\s(])~([^~\n]+)~(?=[\s).,!?:;]|$)/g, "$1<del>$2</del>");
  html = html.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
  return html.replace(/\n/g, "<br/>");
}

/** Mensajes que el backend guarda como "[location]", "[contacts]", etc. */
const SPECIAL_TYPES: Record<string, { icon: string; label: string }> = {
  location: { icon: "geo-alt-fill", label: "Ubicación compartida" },
  contacts: { icon: "person-vcard", label: "Contacto compartido" },
  interactive: { icon: "ui-checks", label: "Respuesta a mensaje interactivo" },
  button: { icon: "hand-index", label: "Respuesta de botón" },
  order: { icon: "cart", label: "Pedido" },
  unsupported: { icon: "question-circle", label: "Mensaje no soportado" },
};

export function getSpecialMessage(body?: string | null, contentType?: string | null): { icon: string; label: string } | null {
  const match = body?.trim().match(/^\[([a-z_]+)\]$/i);
  const type = match ? match[1].toLowerCase() : null;
  if (type) {
    return SPECIAL_TYPES[type] ?? { icon: "question-circle", label: `Mensaje de tipo "${type}"` };
  }
  if (!body && contentType && ["location", "contacts"].includes(contentType)) {
    return SPECIAL_TYPES[contentType];
  }
  return null;
}

export function getInitials(name?: string | number | null): string {
  if (name === null || name === undefined || name === "") return "?";
  const text = String(name).trim();
  if (/^\+?\d+$/.test(text)) return text.slice(-2);
  const parts = text.split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

const AVATAR_COLORS = ["#25D366", "#34B7F1", "#FF8A65", "#9575CD", "#4DB6AC", "#F06292", "#7986CB", "#FFB74D", "#A1887F", "#4FC3F7"];

/** Color estable por contacto (no depende de la posición en la lista). */
export function avatarColor(seed?: string | number | null): string {
  const text = String(seed ?? "");
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

/** Estados de WhatsApp en la tabla whatsapp_statuses: 4 = entregado, 6 = leído (según uso previo). */
export function statusIcon(statusId?: number | null, localStatus?: string): { icon: string; className: string; label: string } {
  if (localStatus === "sending") return { icon: "clock", className: "text-body-secondary", label: "Enviando" };
  if (localStatus === "failed") return { icon: "exclamation-circle-fill", className: "text-danger", label: "No enviado" };
  if (statusId === 6) return { icon: "check2-all", className: "wa-read", label: "Leído" };
  if (statusId === 4) return { icon: "check2-all", className: "text-body-secondary", label: "Entregado" };
  return { icon: "check2", className: "text-body-secondary", label: "Enviado" };
}

export function displayName(first?: string | null, last?: string | null, number?: string | number | null): string {
  const name = [first, last].filter(Boolean).join(" ").trim();
  return name || (number ? `+${number}` : "Sin nombre");
}
